import apiClient from '../api/config';
import { User } from '../types/user';

// Garage Authentication Types
export interface GarageLoginRequest {
  email: string;
  password: string;
}

export interface GarageLoginResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

// Customer Authentication Types
export interface CustomerLoginRequest {
  phone_number: string;
}

export interface CustomerLoginResponse {
  message: string;
}

export interface CustomerVerifyOTPRequest {
  phone_number: string;
  otp_code: string;
}

export interface CustomerVerifyOTPResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

// Garage Registration Types
export interface GarageRegistrationRequest {
  email: string;
  password: string;
  name: string;
  phoneNumber: string;
  garageName: string;
  garageAddress: string;
  city?: string;
  country?: string;
  referral_code?: string;
}

export interface GarageRegistrationResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

class AuthService {
  // Garage Authentication Methods
  async garageLogin(email: string, password: string): Promise<GarageLoginResponse> {
    const response = await apiClient.post<GarageLoginResponse>('/auth/garage/login', {
      email,
      password,
    });
    
    // Store tokens
    this.storeAuthData(response.data);
    
    return response.data;
  }
  
  async garageRegister(data: GarageRegistrationRequest): Promise<GarageRegistrationResponse> {
    // Transform frontend data to backend format
    const backendData = {
      email: data.email,
      password: data.password,
      name: data.name,
      phone_number: data.phoneNumber,
      garage_name: data.garageName,
      address: data.garageAddress,
      city: data.city || 'Kuwait City', // Use provided city or default
      country: data.country || 'Kuwait', // Use provided country or default
      referral_code: data.referral_code
    };
    
    const response = await apiClient.post<{message: string; data: GarageRegistrationResponse}>('/auth/garage/register', backendData);
    
    // Store tokens
    this.storeAuthData(response.data.data);
    
    return response.data.data;
  }

  // Customer Authentication Methods
  async customerLogin(phoneNumber: string): Promise<CustomerLoginResponse> {
    const response = await apiClient.post<CustomerLoginResponse>('/auth/customer/login', {
      phone_number: phoneNumber,
    });
    return response.data;
  }
  
  async verifyCustomerOTP(phoneNumber: string, otpCode: string): Promise<CustomerVerifyOTPResponse> {
    const response = await apiClient.post<CustomerVerifyOTPResponse>('/auth/customer/verify-otp', {
      phone_number: phoneNumber,
      otp_code: otpCode,
    });
    
    // Store tokens
    this.storeAuthData(response.data);
    
    return response.data;
  }
  
  // Common Methods
  async logout(): Promise<void> {
    try {
      // Try to call logout endpoint to invalidate server-side session
      const token = localStorage.getItem('access_token');
      if (token) {
        await apiClient.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // Ignore logout API errors - we'll clear local state anyway
      console.log('Logout API call failed, clearing local state:', error);
    } finally {
      // Always clear local storage regardless of API call success
      this.clearAuthData();
    }
  }

  // Public method to clear all auth data
  clearAuthData(): void {
    console.log('AuthService: Clearing auth data');
    console.trace('AuthService: clearAuthData called from:');
    const hadTokenBefore = !!localStorage.getItem('access_token');
    console.log('AuthService: Had token before clearing:', hadTokenBefore);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    console.log('AuthService: Auth data cleared');
    // Clear any other auth-related data
  }
  
  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }
    
    const response = await apiClient.post<{access_token: string, refresh_token: string}>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    
    // Update tokens
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);
    
    return response.data.access_token;
  }
  
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }
  
  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user ? user.role : null;
  }
  
  isGarageUser(): boolean {
    const role = this.getUserRole();
    return role === 'super_admin' || role === 'garage_owner' || role === 'employee';
  }
  
  isCustomer(): boolean {
    return this.getUserRole() === 'customer';
  }
  
  // Helper method to store auth data
  private storeAuthData(data: { access_token: string; refresh_token: string; user: User }) {
    console.log('AuthService: Storing auth data for user:', data.user.name, 'role:', data.user.role);
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // Dispatch event to reset initial load flag
    window.dispatchEvent(new Event('userLoggedIn'));
    
    // Verify storage immediately
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    console.log('AuthService: Storage verification:', { 
      hasToken: !!storedToken, 
      hasUser: !!storedUser,
      tokenLength: storedToken?.length || 0
    });
  }
}

export const authService = new AuthService();
export default authService;